<app-page-shell>
  <app-page-hero eyebrow="POSM" title="POSM Executions" subtitle="Manage client rollouts, delivery, removal, site survey, collection, and rebranding execution work.">
    <div actions>
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadProjects()"></p-button>
      <p-button label="Delivery Schedules" icon="pi pi-calendar" severity="secondary" [outlined]="true" routerLink="/posm/executions/delivery-schedules"></p-button>
      <p-button label="New Execution" icon="pi pi-plus" (click)="createProject()"></p-button>
    </div>
  </app-page-hero>

  <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

  <app-page-search-panel title="Search POSM Executions" [activeFilters]="activeSearchCount" [collapsed]="true" (clear)="clearSearch()" (search)="applySearch()">
    <div class="grid p-fluid">
      <div class="field col-12 lg:col-4">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
          <p-iftalabel>
            <input id="posmProjectKeyword" pInputText type="text" [(ngModel)]="searchText" class="w-full">
            <label for="posmProjectKeyword">Keyword</label>
          </p-iftalabel>
        </p-inputGroup>
      </div>

      <div class="field col-12 md:col-6 lg:col-2">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
          <p-iftalabel>
            <p-select inputId="posmProjectPriority" [options]="priorityOptions" optionLabel="label" optionValue="value" [(ngModel)]="priorityFilter" appendTo="body" styleClass="w-full"></p-select>
            <label for="posmProjectPriority">Priority</label>
          </p-iftalabel>
        </p-inputGroup>
      </div>

      <div class="field col-12 md:col-6 lg:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-filter"></i></p-inputGroupAddon>
          <p-iftalabel>
            <p-select inputId="posmProjectStatus" [options]="statusOptions" optionLabel="label" optionValue="value" [(ngModel)]="statusFilter" appendTo="body" styleClass="w-full"></p-select>
            <label for="posmProjectStatus">Execution Status</label>
          </p-iftalabel>
        </p-inputGroup>
      </div>

      <div class="field col-12 md:col-6 lg:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
          <p-iftalabel>
            <input id="posmProjectJobType" pInputText [(ngModel)]="jobTypeFilter" class="w-full">
            <label for="posmProjectJobType">Job Type</label>
          </p-iftalabel>
        </p-inputGroup>
      </div>

      <div class="field col-12 md:col-6 lg:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
          <p-iftalabel>
            <input id="posmProjectClient" pInputText [(ngModel)]="clientFilter" class="w-full">
            <label for="posmProjectClient">Client</label>
          </p-iftalabel>
        </p-inputGroup>
      </div>

      <div class="field col-12 md:col-6 lg:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon>
          <p-iftalabel>
            <input id="posmProjectBrand" pInputText [(ngModel)]="brandFilter" class="w-full">
            <label for="posmProjectBrand">Brand</label>
          </p-iftalabel>
        </p-inputGroup>
      </div>
    </div>
  </app-page-search-panel>

  <app-page-data-panel
    title="Execution Directory"
    subtitle="Client linkage, scope, progress, timeline pressure, and latest execution state in one view."
    [resultText]="projects.length + ' of ' + totalProjects + ' records'">
    <p-table
      [value]="projects"
      [loading]="loading"
      [paginator]="true"
      [lazy]="true"
      [first]="page * pageSize"
      [totalRecords]="totalProjects"
      (onPage)="onPage($event)"
      [rows]="pageSize"
      [showCurrentPageReport]="true"
      currentPageReportTemplate="Showing {first}-{last} of {totalRecords} executions"
      [rowsPerPageOptions]="[10, 25, 50]"
      styleClass="p-datatable-sm posm-execution-table">

      <ng-template pTemplate="header">
        <tr class="surface-50 text-600 text-sm border-bottom-1 surface-border">
          <th class="action-col">Actions</th>
          <th>Execution</th>
          <th>Client / Brand</th>
          <th>Scope</th>
          <th>Timeline & Progress</th>
          <th>Latest Status</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-project>
        <tr class="border-bottom-1 surface-border">
          <td class="action-col">
            <div class="action-stack">
              <p-button
                icon="pi pi-eye"
                severity="info"
                [text]="true"
                [rounded]="true"
                title="Open execution details"
                (click)="viewProject(project)">
              </p-button>
              @if (project.logisticsProjectId) {
                <p-button
                  icon="pi pi-sitemap"
                  severity="success"
                  [text]="true"
                  [rounded]="true"
                  title="Open linked logistics project"
                  (click)="openLogisticsProject(project)">
                </p-button>
              }
            </div>
          </td>

          <td>
            <div class="cell-title">{{ project.projectName }}</div>
            <div class="cell-meta mt-1">Ref: {{ project.projectNumber || '-' }}</div>
            <div class="tag-row mt-2">
              <p-tag [value]="projectPriorityLabel(project)" [severity]="projectPrioritySeverity(project)"></p-tag>
              @if (project.logisticsProjectId) {
                <p-tag value="Linked Logistics" severity="success"></p-tag>
              }
            </div>
          </td>

          <td>
            <div class="cell-title">{{ projectClientName(project) }}</div>
            <div class="cell-meta mt-1">Brand: {{ projectBrandName(project) }}</div>
            <div class="cell-meta mt-1">Account: {{ projectClientAccount(project) }}</div>
            <div class="cell-meta mt-1">Contact: {{ projectContact(project) }}</div>
          </td>

          <td>
            <div class="tag-row">
              @for (jobType of projectJobTypes(project); track jobType) {
                <p-tag [value]="jobType" severity="info"></p-tag>
              }
              @if (!projectJobTypes(project).length) {
                <p-tag [value]="projectJobTypeLabel(project)" severity="info"></p-tag>
              }
            </div>
            <div class="cell-meta mt-2">Group: {{ projectJobTypeGroup(project) }}</div>
            <div class="cell-meta mt-1">Strategy: {{ projectExecutionStrategy(project) }}</div>
            <div class="cell-meta mt-1">Merchandiser: {{ projectMerchandiser(project) }}</div>
          </td>

          <td>
            <div class="cell-title">Start: {{ project.startDate | date:'dd MMM yyyy' }}</div>
            <div class="cell-meta mt-1">Deadline: {{ project.deadline | date:'dd MMM yyyy' }}</div>

            <div class="progress-block mt-3">
              <div class="progress-header">
                <span>Execution Progress</span>
                @if (hasTaskProgress(project)) {
                  <strong>{{ projectProgress(project) }}%</strong>
                } @else if (summaryLoading) {
                  <strong>Loading</strong>
                } @else {
                  <strong>Pending</strong>
                }
              </div>

              @if (hasTaskProgress(project)) {
                <p-progressBar
                  [value]="projectProgress(project)"
                  [showValue]="false"
                  styleClass="execution-progress">
                </p-progressBar>
              } @else if (summaryLoading) {
                <p-progressBar mode="indeterminate" [showValue]="false" styleClass="execution-progress loading-progress"></p-progressBar>
              }

              <div class="cell-meta mt-2">{{ projectProgressCaption(project) }}</div>
            </div>
          </td>

          <td>
            <div class="tag-row">
              <p-tag [value]="projectStatusLabel(project)" [severity]="projectStatusSeverity(project)"></p-tag>
              <p-tag [value]="projectLifecycleLabel(project)" [severity]="projectLifecycleSeverity(project)"></p-tag>
            </div>
            <div class="cell-meta mt-2">Updated: {{ project.updatedAt | date:'dd MMM yyyy, h:mm a' }}</div>
            <div class="cell-meta mt-1">By: {{ projectUpdatedBy(project) }}</div>
            @if (project.closedAt) {
              <div class="cell-meta mt-1">Closed: {{ project.closedAt | date:'dd MMM yyyy' }}</div>
            } @else if (project.reopenedAt) {
              <div class="cell-meta mt-1">Reopened: {{ project.reopenedAt | date:'dd MMM yyyy' }}</div>
            }
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="text-center p-4 text-500">No POSM executions found.</td>
        </tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>
</app-page-shell>
